import { useState, useEffect, useRef } from "react";

/**
 * ChaosHamster
 *
 * A frantic hamster powering your app from inside a wheel.
 * Does not know the wheel is on fire. Would not stop even if it did.
 *
 * Props:
 *   mode      — "normal" | "overclock" | "meltdown"
 *   label     — string shown below the hamster (default varies by mode)
 *   size      — number, SVG render size in px (default 200)
 *   showStats — bool, show the live telemetry readout (default false)
 */
export default function ChaosHamster({
  mode = "normal",
  label,
  size = 200,
  showStats = false,
}) {
  const cfg = {
    normal: {
      runSpeed:    "0.28s",
      wheelSpeed:  "0.55s",
      flickerSpeed:"0.45s",
      sweatDelay:  "0.9s",
      fireScale:   1,
      fireColor:   "#f97316",
      fireInner:   "#fbbf24",
      fireCore:    "#fef08a",
      eyeSize:     5,
      cheekScale:  1,
      defaultLabel:"loading…",
      rpm:         "3,200 rpm",
      temp:        "37°C",
      status:      "nominal",
      statusColor: "#16a34a",
      shake:       false,
      cracksVisible: false,
    },
    overclock: {
      runSpeed:    "0.13s",
      wheelSpeed:  "0.22s",
      flickerSpeed:"0.2s",
      sweatDelay:  "0.45s",
      fireScale:   1.35,
      fireColor:   "#ea580c",
      fireInner:   "#f97316",
      fireCore:    "#fde68a",
      eyeSize:     6.5,
      cheekScale:  1.1,
      defaultLabel:"turbo mode…",
      rpm:         "9,800 rpm",
      temp:        "94°C",
      status:      "overclock",
      statusColor: "#d97706",
      shake:       true,
      shakeMag:    1.5,
      cracksVisible: false,
    },
    meltdown: {
      runSpeed:    "0.06s",
      wheelSpeed:  "0.09s",
      flickerSpeed:"0.1s",
      sweatDelay:  "0.22s",
      fireScale:   1.75,
      fireColor:   "#dc2626",
      fireInner:   "#ef4444",
      fireCore:    "#fca5a5",
      eyeSize:     8,
      cheekScale:  1.25,
      defaultLabel:"SEND HELP",
      rpm:         "∞ rpm",
      temp:        "⚠ UNKNOWN",
      status:      "meltdown",
      statusColor: "#dc2626",
      shake:       true,
      shakeMag:    3,
      cracksVisible: true,
    },
  }[mode] || cfg.normal;

  const displayLabel = label ?? cfg.defaultLabel;

  // live tick counter for the stats panel
  const [ticks, setTicks] = useState(0);
  const tickRef = useRef(null);
  useEffect(() => {
    if (!showStats) return;
    const interval = mode === "meltdown" ? 60 : mode === "overclock" ? 120 : 300;
    tickRef.current = setInterval(() => setTicks(t => t + 1), interval);
    return () => clearInterval(tickRef.current);
  }, [mode, showStats]);

  const animStyle = (name, dur, delay = "0s", easing = "ease-in-out", iter = "infinite") =>
    ({ animation: `${name} ${dur} ${easing} ${delay} ${iter}` });

  const shakeStyle = cfg.shake
    ? { animation: `hamShake ${mode === "meltdown" ? "0.08s" : "0.15s"} ease-in-out infinite` }
    : {};

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        fontFamily: "var(--font-sans)",
      }}
    >
      <svg
        viewBox="0 0 200 200"
        width={size}
        height={size}
        aria-hidden="true"
        style={{ overflow: "visible" }}
      >
        <defs>
          <clipPath id="wheelClip">
            <circle cx="100" cy="138" r="46" />
          </clipPath>
        </defs>

        <style>{`
          @keyframes hamRun {
            0%,100% { transform: translateY(0) scaleX(1); }
            25%      { transform: translateY(-3px) scaleX(1.05); }
            75%      { transform: translateY(2px) scaleX(0.95); }
          }
          @keyframes hamWheel {
            to { transform: rotate(360deg); }
          }
          @keyframes hamFlicker {
            0%,100% { transform: scaleY(1) scaleX(1); opacity: 1; }
            33%     { transform: scaleY(0.75) scaleX(1.1); opacity: 0.85; }
            66%     { transform: scaleY(1.2) scaleX(0.9); opacity: 0.95; }
          }
          @keyframes hamFlickerFast {
            0%,100% { transform: scaleY(1) scaleX(1); opacity: 1; }
            25%     { transform: scaleY(0.6) scaleX(1.15); opacity: 0.7; }
            75%     { transform: scaleY(1.3) scaleX(0.85); opacity: 0.9; }
          }
          @keyframes hamSweat {
            0%   { opacity: 0; transform: translate(0, 0) scale(1); }
            25%  { opacity: 1; }
            100% { opacity: 0; transform: translate(-5px, 14px) scale(0.6); }
          }
          @keyframes hamBlink {
            0%,88%,100% { transform: scaleY(1); }
            94%          { transform: scaleY(0.08); }
          }
          @keyframes hamWobbleEye {
            0%,100% { transform: scale(1); }
            50%      { transform: scale(1.25); }
          }
          @keyframes hamShake {
            0%,100% { transform: translateX(0); }
            25%     { transform: translateX(var(--shake-x, 2px)); }
            75%     { transform: translateX(calc(var(--shake-x, 2px) * -1)); }
          }
          @keyframes hamStar {
            0%   { opacity: 0; transform: translate(0,0) rotate(0deg) scale(0.5); }
            20%  { opacity: 1; }
            100% { opacity: 0; transform: translate(12px,-22px) rotate(200deg) scale(1.2); }
          }
          @keyframes hamCrack {
            0%,100% { opacity: 0.4; }
            50%      { opacity: 0.85; }
          }
          @keyframes hamSmoke {
            0%   { opacity: 0.7; transform: translateY(0) scale(0.8); }
            100% { opacity: 0;   transform: translateY(-28px) scale(1.4); }
          }
        `}</style>

        {/* ── smoke puffs (meltdown only) ── */}
        {mode === "meltdown" && [0,1,2].map(i => (
          <ellipse
            key={i}
            cx={88 + i * 12} cy={70}
            rx={6 + i * 2} ry={5 + i}
            fill="#9ca3af"
            style={{
              ...animStyle("hamSmoke", "1.1s", `${i * 0.28}s`, "ease-out"),
              transformOrigin: `${88 + i * 12}px 70px`,
            }}
          />
        ))}

        {/* ── fire base ── */}
        <g style={{ transform: `scale(${cfg.fireScale})`, transformOrigin: "100px 158px" }}>
          {/* outer flames */}
          {[
            { cx: 76,  cy: 158, rx: 13, ry: 8,  col: cfg.fireColor,  delay: "0s"    },
            { cx: 100, cy: 154, rx: 18, ry: 11, col: cfg.fireColor,  delay: "0.07s" },
            { cx: 124, cy: 158, rx: 13, ry: 8,  col: cfg.fireColor,  delay: "0.04s" },
          ].map((f, i) => (
            <ellipse key={i} cx={f.cx} cy={f.cy} rx={f.rx} ry={f.ry} fill={f.col}
              style={{
                ...animStyle(mode === "meltdown" ? "hamFlickerFast" : "hamFlicker", cfg.flickerSpeed, f.delay),
                transformOrigin: `${f.cx}px ${f.cy}px`,
              }}
            />
          ))}
          {/* inner flames */}
          {[
            { cx: 80,  cy: 155, rx: 8,  ry: 6,  col: cfg.fireInner, delay: "0.1s"  },
            { cx: 100, cy: 150, rx: 11, ry: 8,  col: cfg.fireInner, delay: "0s"    },
            { cx: 120, cy: 155, rx: 8,  ry: 6,  col: cfg.fireInner, delay: "0.15s" },
          ].map((f, i) => (
            <ellipse key={i} cx={f.cx} cy={f.cy} rx={f.rx} ry={f.ry} fill={f.col}
              style={{
                ...animStyle(mode === "meltdown" ? "hamFlickerFast" : "hamFlicker", cfg.flickerSpeed, f.delay),
                transformOrigin: `${f.cx}px ${f.cy}px`,
              }}
            />
          ))}
          {/* core */}
          <ellipse cx={100} cy={148} rx={6} ry={4} fill={cfg.fireCore}
            style={{
              ...animStyle(mode === "meltdown" ? "hamFlickerFast" : "hamFlicker", cfg.flickerSpeed, "0.05s"),
              transformOrigin: "100px 148px",
            }}
          />
        </g>

        {/* ── wheel track ── */}
        <rect x={44} y={160} width={112} height={20} rx={7} fill="#374151" />

        {/* ── spinning wheel ── */}
        <g style={{ transformOrigin: "100px 138px", ...animStyle("hamWheel", cfg.wheelSpeed, "0s", "linear") }}>
          <circle cx={100} cy={138} r={44} fill="none" stroke="#4b5563" strokeWidth={5} />
          {[0,45,90,135].map(a => (
            <line key={a}
              x1={100 + Math.cos(a * Math.PI/180) * 44}
              y1={138 + Math.sin(a * Math.PI/180) * 44}
              x2={100 - Math.cos(a * Math.PI/180) * 44}
              y2={138 - Math.sin(a * Math.PI/180) * 44}
              stroke="#4b5563" strokeWidth={3}
            />
          ))}
          {/* cracks on wheel rim for meltdown */}
          {cfg.cracksVisible && (
            <g stroke="#ef4444" strokeWidth={1.5} fill="none"
              style={{ animation: "hamCrack 0.3s ease-in-out infinite" }}>
              <path d="M 100 94 l 4 8 l -3 5" />
              <path d="M 136 120 l -7 5 l 2 6" />
              <path d="M 72 158 l 5 -7 l -4 -4" />
            </g>
          )}
        </g>
        {/* axle hub */}
        <circle cx={100} cy={138} r={7} fill="#6b7280" />
        <circle cx={100} cy={138} r={3} fill="#374151" />

        {/* ── hamster (inside wheel, clipped) ── */}
        <g clipPath="url(#wheelClip)">
          <g style={{ ...shakeStyle, "--shake-x": `${cfg.shakeMag || 2}px` }}>
            <g style={{
              transformOrigin: "100px 125px",
              ...animStyle("hamRun", cfg.runSpeed),
            }}>
              {/* body */}
              <ellipse cx={100} cy={130} rx={26} ry={21} fill="#f59e0b" />
              {/* belly */}
              <ellipse cx={100} cy={135} rx={16} ry={13} fill="#fde68a" />
              {/* cheek pouches — the glory */}
              <ellipse cx={74}  cy={126} rx={13 * cfg.cheekScale} ry={12 * cfg.cheekScale} fill="#f59e0b" />
              <ellipse cx={126} cy={126} rx={13 * cfg.cheekScale} ry={12 * cfg.cheekScale} fill="#f59e0b" />
              <ellipse cx={73}  cy={126} rx={9  * cfg.cheekScale} ry={8  * cfg.cheekScale} fill="#fbbf24" />
              <ellipse cx={127} cy={126} rx={9  * cfg.cheekScale} ry={8  * cfg.cheekScale} fill="#fbbf24" />
              {/* head */}
              <ellipse cx={100} cy={112} rx={22} ry={20} fill="#f59e0b" />
              {/* ears */}
              <ellipse cx={80}  cy={95} rx={9} ry={8} fill="#f59e0b" />
              <ellipse cx={120} cy={95} rx={9} ry={8} fill="#f59e0b" />
              <ellipse cx={80}  cy={95} rx={5} ry={4} fill="#fda4af" />
              <ellipse cx={120} cy={95} rx={5} ry={4} fill="#fda4af" />
              {/* eyes */}
              <g style={{
                transformOrigin: "88px 110px",
                ...animStyle(mode === "meltdown" ? "hamWobbleEye" : "hamBlink", mode === "meltdown" ? "0.25s" : "3.2s"),
              }}>
                <ellipse cx={88} cy={110} rx={cfg.eyeSize} ry={cfg.eyeSize} fill="white" />
              </g>
              <g style={{
                transformOrigin: "112px 110px",
                ...animStyle(mode === "meltdown" ? "hamWobbleEye" : "hamBlink", mode === "meltdown" ? "0.25s" : "3.2s", "0.15s"),
              }}>
                <ellipse cx={112} cy={110} rx={cfg.eyeSize} ry={cfg.eyeSize} fill="white" />
              </g>
              {/* pupils */}
              <circle cx={90}  cy={110} r={cfg.eyeSize * 0.58} fill="#1c1917" />
              <circle cx={114} cy={110} r={cfg.eyeSize * 0.58} fill="#1c1917" />
              {/* eye glints */}
              <circle cx={91}  cy={108} r={cfg.eyeSize * 0.22} fill="white" />
              <circle cx={115} cy={108} r={cfg.eyeSize * 0.22} fill="white" />
              {/* stress lines around eyes (overclock / meltdown) */}
              {mode !== "normal" && (
                <g stroke="#92400e" strokeWidth={1} fill="none" strokeLinecap="round">
                  <line x1={78} y1={104} x2={74} y2={101} />
                  <line x1={76} y1={108} x2={71} y2={108} />
                  <line x1={122} y1={104} x2={126} y2={101} />
                  <line x1={124} y1={108} x2={129} y2={108} />
                </g>
              )}
              {/* nose */}
              <ellipse cx={100} cy={118} rx={4} ry={3} fill="#fda4af" />
              {/* mouth — gets more distressed by mode */}
              {mode === "normal" && (
                <path d="M 94 122 Q 100 126 106 122" fill="none" stroke="#92400e" strokeWidth={1.5} strokeLinecap="round" />
              )}
              {mode === "overclock" && (
                <path d="M 92 122 Q 100 128 108 122" fill="none" stroke="#92400e" strokeWidth={1.5} strokeLinecap="round" />
              )}
              {mode === "meltdown" && (
                <>
                  <path d="M 91 124 Q 100 132 109 124" fill="none" stroke="#92400e" strokeWidth={2} strokeLinecap="round" />
                  {/* tongue hanging out */}
                  <ellipse cx={100} cy={128} rx={5} ry={4} fill="#f9a8d4" />
                </>
              )}
              {/* tiny legs */}
              <rect x={86} y={147} width={10} height={7} rx={4} fill="#d97706" />
              <rect x={104} y={147} width={10} height={7} rx={4} fill="#d97706" />
              {/* tail */}
              <path d="M 124 132 Q 134 124 132 116" fill="none" stroke="#f59e0b" strokeWidth={4} strokeLinecap="round" />
            </g>
          </g>
        </g>

        {/* ── sweat drops ── */}
        {[
          { cx: 60,  cy: 102, delay: "0s"   },
          { cx: 142, cy: 98,  delay: `${parseFloat(cfg.sweatDelay) * 0.33}s` },
          { cx: 55,  cy: 118, delay: `${parseFloat(cfg.sweatDelay) * 0.66}s` },
          ...(mode !== "normal" ? [{ cx: 148, cy: 116, delay: `${parseFloat(cfg.sweatDelay) * 0.5}s` }] : []),
          ...(mode === "meltdown" ? [
            { cx: 50,  cy: 132, delay: "0.1s" },
            { cx: 152, cy: 130, delay: "0.2s" },
          ] : []),
        ].map((s, i) => (
          <ellipse key={i} cx={s.cx} cy={s.cy} rx={3} ry={5} fill="#60a5fa"
            style={{ ...animStyle("hamSweat", cfg.sweatDelay, s.delay, "ease-out") }}
          />
        ))}

        {/* ── speed stars ── */}
        {[
          { x: 34,  y: 128, delay: "0s",    size: 11 },
          { x: 158, y: 122, delay: "0.38s", size: 9  },
          ...(mode !== "normal" ? [
            { x: 30,  y: 112, delay: "0.18s", size: 8 },
            { x: 162, y: 136, delay: "0.55s", size: 7 },
          ] : []),
          ...(mode === "meltdown" ? [
            { x: 26,  y: 140, delay: "0.08s", size: 10 },
            { x: 166, y: 108, delay: "0.42s", size: 9  },
          ] : []),
        ].map((s, i) => (
          <text key={i} x={s.x} y={s.y}
            fill="#fbbf24"
            fontSize={s.size}
            fontWeight="700"
            textAnchor="middle"
            style={{ ...animStyle("hamStar", cfg.sweatDelay, s.delay, "ease-out") }}
          >
            ✦
          </text>
        ))}
      </svg>

      {/* ── label ── */}
      {displayLabel && (
        <p style={{
          margin: 0,
          fontSize: 13,
          color: "var(--color-text-secondary)",
          fontFamily: "var(--font-sans)",
          letterSpacing: mode === "meltdown" ? "0.04em" : "normal",
        }}>
          {displayLabel}
        </p>
      )}

      {/* ── telemetry panel ── */}
      {showStats && (
        <div style={{
          marginTop: 4,
          padding: "8px 14px",
          background: "var(--color-background-secondary)",
          borderRadius: "var(--border-radius-md)",
          border: "0.5px solid var(--color-border-tertiary)",
          fontSize: 12,
          color: "var(--color-text-secondary)",
          fontFamily: "var(--font-mono)",
          display: "grid",
          gridTemplateColumns: "auto auto",
          gap: "3px 16px",
          minWidth: 180,
        }}>
          <span>speed</span>
          <span style={{ color: "var(--color-text-primary)", fontWeight: 500 }}>{cfg.rpm}</span>
          <span>core temp</span>
          <span style={{ color: cfg.statusColor, fontWeight: 500 }}>{cfg.temp}</span>
          <span>status</span>
          <span style={{ color: cfg.statusColor, fontWeight: 500 }}>{cfg.status}</span>
          <span>cycles</span>
          <span style={{ color: "var(--color-text-primary)", fontWeight: 500 }}>{ticks.toLocaleString()}</span>
        </div>
      )}
    </div>
  );
}